/**
 * VprIndexStatus Component
 *
 * Displays CLIP index status with build/rebuild controls.
 * New component specific to VPR (no Room Recognition equivalent).
 */

import React from 'react';
import {
  Database,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Clock,
  Layers,
  Cpu
} from 'lucide-react';

const VprIndexStatus = ({
  indexStatus,
  roomCount,
  isBuilding,
  indexNeedsRebuild,
  onBuildIndex
}) => {
  const hasIndex = !!(indexStatus && indexStatus.published);
  const canPublish = roomCount >= 2;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-lg p-5">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
            <Database className="w-5 h-5 text-cyan-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">V2 Publication</h3>
            <p className="text-sm text-gray-500">
              {hasIndex
                ? indexNeedsRebuild
                  ? 'Index is stale — rooms changed since last build'
                  : 'Index is up to date'
                : 'Not published to visitors yet'}
            </p>
          </div>
        </div>

        {/* Status Badge */}
        <div className="flex items-center gap-2">
          {hasIndex ? (
            indexNeedsRebuild ? (
              <span className="flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                <AlertTriangle className="w-3.5 h-3.5" />
                Stale
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                <CheckCircle className="w-3.5 h-3.5" />
                Current
              </span>
            )
          ) : (
            <span className="flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-500 rounded-full text-xs font-medium">
              Not Built
            </span>
          )}
        </div>
      </div>

      {/* Stale Warning */}
      {indexNeedsRebuild && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-yellow-700">
            Room mappings changed. Publish V2 again before visitor scans can use them.
          </p>
        </div>
      )}

      {/* Index Details Grid */}
      {hasIndex && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Version</p>
            <p className="text-sm font-medium text-gray-900">v{indexStatus.version}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Rooms Indexed</p>
            <p className="text-sm font-medium text-gray-900">{indexStatus.roomCount || 0}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Index Size</p>
            <p className="text-sm font-medium text-gray-900">{indexStatus.referenceCount || 0}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Last Built</p>
            <p className="text-sm font-medium text-gray-900">{formatDate(indexStatus.updatedAt)}</p>
          </div>
        </div>
      )}

      {/* Model Info */}
      <div className="flex items-center gap-4 mb-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <Cpu className="w-3.5 h-3.5" />
          CLIP ViT-B/32
        </span>
        <span className="flex items-center gap-1">
          <Layers className="w-3.5 h-3.5" />
          512-d embeddings
        </span>
        <span className="flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" />
          Cosine similarity
        </span>
      </div>

      {/* Build Button */}
      <button
        onClick={onBuildIndex}
        disabled={isBuilding || !canPublish}
        className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
          isBuilding || !canPublish
            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
            : indexNeedsRebuild
            ? 'bg-yellow-500 text-white hover:bg-yellow-600'
            : hasIndex
            ? 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200'
            : 'bg-cyan-600 text-white hover:bg-cyan-700'
        }`}
      >
        {isBuilding ? (
          <>
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            Building Index...
          </>
        ) : (
          <>
            <RefreshCw className="w-4 h-4" />
            {!canPublish
              ? 'Add at least two rooms first'
              : hasIndex
              ? indexNeedsRebuild
                ? 'Publish V2 Changes'
                : 'Publish V2 Again'
              : 'Publish Room Recognition V2'}
          </>
        )}
      </button>
    </div>
  );
};

export default VprIndexStatus;
